import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Pictures.css";
import ActionButton from "../../ActionButton";
import { birds } from "../../../data/birds";

// Fotos em destaque no empilhado. O CSS posiciona as imagens com nth-child(1..3),
// então a lista precisa ter exatamente 3 itens.
// Cada item aponta para uma ave de birds.ts (birdId) e para a posição da foto em
// bird.photos (photoIndex: 0 = primeira foto).
const FEATURED_PHOTOS = [
  { birdId: 9, photoIndex: 0 }, // beija-flor-tesoura
  { birdId: 89, photoIndex: 0 }, // garça-branca-grande
  { birdId: 12, photoIndex: 0 }, // socozinho
];

// popularName vem em minúsculas no dicionário ("garça-branca-grande")
const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

// Resolve cada destaque para { src, alt } a partir do dicionário.
// Itens cuja ave/foto não existir são ignorados em vez de gerar <img> quebrada.
const featuredImages = FEATURED_PHOTOS.flatMap(({ birdId, photoIndex }) => {
  const bird = birds.find((b) => b.id === birdId);
  const photo = bird?.photos[photoIndex];
  return bird && photo
    ? [
        {
          src: photo.src,
          alt: `${capitalize(bird.popularName)} (${bird.scientificName})`,
        },
      ]
    : [];
});

const Pictures: React.FC = () => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isHovering && featuredImages.length > 1) {
      intervalRef.current = setInterval(() => {
        setFocusedIndex((prev) => (prev + 1) % featuredImages.length);
      }, 3000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setFocusedIndex(0);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovering]);

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  return (
    <div className="pictures">
      <div
        className="pictures-stack"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {featuredImages.map(({ src, alt }, index) => (
          <img
            key={src}
            src={src}
            alt={alt}
            className={`pictures-image ${isHovering ? "hovering" : ""}`}
            style={{
              zIndex: focusedIndex === index ? 4 : 3 - index,
            }}
            width={100}
            height={100}
          />
        ))}
      </div>
      <div className="pictures-content">
        <h2 className="pictures-title">Fotos</h2>
        <p className="pictures-description">
          Esta coleção de fotos registra os encontros entre lente e natureza nos
          campi da UFRJ. Cada imagem é um recorte da vida das aves: seus voos,
          cores, gestos e presenças silenciosas que compõem a paisagem
          cotidiana. Mais do que registros, são olhares atentos transformados em
          imagem.
        </p>
        <div className="pictures-button-row">
          <ActionButton
            text="Ver Galeria"
            color="var(--button-primary)"
            hoverColor="var(--button-primary-hover)"
            onClick={() => navigate("/fotos")}
          />
        </div>
      </div>
    </div>
  );
};

export default Pictures;
